import React, { useEffect, useReducer, useRef, useState } from "react";

import { Input, InputChangeEvent, SwitchChangeEvent } from "@progress/kendo-react-inputs";
import { getInitialState } from "@selas/state-management";
import { EntityEditor, handleChange, IAddEntityScreenProps, IEditEntityScreenProps, YesNoSwitch } from "@selas/ui-components";
import { isNullOrEmpty } from "@selas/utils";
import { useTranslation } from "react-i18next";

import Endpoint from "../../../services/api/endpoint";
import { jobFunctionReducer } from "../../../state/reducers";
import { IJobFunction } from "../../../utils/types/models";

const JobFunctionEditor: React.FC<IAddEntityScreenProps<IJobFunction> | IEditEntityScreenProps<IJobFunction>> = (props: IAddEntityScreenProps<IJobFunction> | IEditEntityScreenProps<IJobFunction>) => {
	const { t } = useTranslation();

	const [state, dispatch] = useReducer(jobFunctionReducer, getInitialState<IJobFunction>());
	const initialJobFunction: IJobFunction = {
		id: (props as IEditEntityScreenProps<IJobFunction>).recordId || 0,
		name: "",
		active: true
	};
	const [jobFunction, setJobfunction] = useState<IJobFunction>(initialJobFunction);
	const [dataChanged, setDataChanged] = useState<boolean>(false);
	const firstField: React.MutableRefObject<Input> = useRef();

	useEffect(() => {
		if (state.entity) {
			setJobfunction(state.entity);
		}
	}, [state.entity]);

	function onChange(event: InputChangeEvent | SwitchChangeEvent): void {
		setJobfunction(handleChange(jobFunction, event));
		setDataChanged(true);
	}

	function getErrorMessages(): string[] {
		const messages: string[] = [];
		if (isNullOrEmpty(jobFunction.name)) {
			messages.push(t("fill_in_required_field", { field: t("name").toLowerCase() }));
		}
		return messages;
	}

	function onActionButtonClicked(close: boolean, record: IJobFunction): void {
		if (record && !close) {
			setJobfunction(record);
			setDataChanged(false);
		}
		props.actionButtonClicked(close, record);
	}

	const readonly: boolean = (props as IEditEntityScreenProps<IJobFunction>).readonly;

	return (
		<EntityEditor
			width="70%"
			record={jobFunction}
			endpoint={Endpoint.JobFunctions}
			entityState={state}
			entityType={t("jobFunction")}
			dispatch={dispatch}
			dataChanged={dataChanged}
			readonly={readonly}
			recordName={jobFunction.name}
			actionButtonClicked={onActionButtonClicked}
			getErrorMessages={getErrorMessages}
			firstFieldRef={firstField}
		>
			<div className="k-form">
				<label className="k-form-field">
					<span>{t("name")} *</span>
					<Input name="name" ref={firstField} value={jobFunction.name} onChange={onChange} required disabled={readonly} />
				</label>
				{!props.hideActive && (
					<div className="k-form-field">
						<div>{t("active")}</div>
						<YesNoSwitch name="active" checked={jobFunction.active} onChange={onChange} disabled={readonly} />
					</div>
				)}
			</div>
		</EntityEditor>
	);
};

export default JobFunctionEditor;
